import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils';
import { getApiUrl } from '../config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Component to display patient name from PostgreSQL
export function PatientNameDisplay({ caseId }: { caseId: string }) {
  const { t } = useTranslation();
  const [name, setName] = useState<string>(t('common.loading'));
  const [error] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  useEffect(() => {
    // Prevent multiple calls for the same caseId
    if (hasFetched) return;
    
    const fetchPatientName = async () => {
      try {
        setHasFetched(true);
        const response = await fetch(getApiUrl(`/api/patients/${caseId}`));
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.patient && data.patient.first_name && data.patient.last_name) {
            setName(`${data.patient.first_name} ${data.patient.last_name}`);
          } else {
            setName('Name Not Available');
          }
        } else if (response.status === 404) {
          setName('Patient Not Found');
        } else {
          setName('Error Loading');
        }
      } catch (err) {
        setName('Error Loading');
      }
    };

    fetchPatientName();
  }, [caseId, hasFetched]);

  if (error) {
    return <span style={{ color: '#ff6b6b' }}>Error</span>;
  }

  return <span>{name}</span>;
}

// Component to display patient date of birth from PostgreSQL
export function PatientDOBDisplay({ caseId }: { caseId: string }) {
  const { t } = useTranslation();
  const [dob, setDob] = useState<string>(t('common.loading'));
  const [error] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  useEffect(() => {
    // Prevent multiple calls for the same caseId
    if (hasFetched) return;
    
    const fetchPatientDOB = async () => {
      try {
        setHasFetched(true);
        const response = await fetch(getApiUrl(`/api/patients/${caseId}`));
        if (response.ok) {
          const data = await response.json();
          setDob(formatDate(data.patient.date_of_birth));
        } else if (response.status === 404) {
          setDob('Patient Not Found');
        } else {
          setDob('Error Loading');
        }
      } catch (err) {
        setDob('Error Loading');
      }
    };

    fetchPatientDOB();
  }, [caseId, hasFetched]);

  if (error) {
    return <span style={{ color: '#ff6b6b' }}>Error</span>;
  }

  return <span>{dob}</span>;
}

// Component to display patient notes from Firebase
export function PatientNotesDisplay({ caseId }: { caseId: string }) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState<string>(t('common.loading'));
  const [error] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  useEffect(() => {
    // Prevent multiple calls for the same caseId
    if (hasFetched) return;
    
    const fetchPatientNotes = async () => {
      try {
        setHasFetched(true);
        // Load notes from Firebase instead of PostgreSQL
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const patientDocRef = doc(db, 'patients', caseId);
        const patientDoc = await getDoc(patientDocRef);
        
        if (patientDoc.exists()) {
          const firebaseData = patientDoc.data();
          const patientNotes = firebaseData.notes || '';
          // Truncate notes if too long for table display
          setNotes(patientNotes.length > 50 ? patientNotes.substring(0, 50) + '...' : patientNotes);
        } else {
          setNotes('N/A');
        }
      } catch (err) {
        setNotes('Error Loading');
      }
    };

    fetchPatientNotes();
  }, [caseId, hasFetched]);

  if (error) {
    return <span style={{ color: '#ff6b6b' }}>Error</span>;
  }

  return <span title={notes === 'N/A' ? '' : notes}>{notes || t('patients.noNotes')}</span>;
}

// Component to display and edit patient notes from Firebase with inline editing
export function PatientNotesEditable({ 
  caseId, 
  onUpdate,
  isEditing: isEditingProp,
  onEditStart,
  onEditEnd
}: { 
  caseId: string; 
  onUpdate?: () => void;
  isEditing?: boolean;
  onEditStart?: () => void;
  onEditEnd?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [notes, setNotes] = useState<string>(t('common.loading'));
  const [fullNotes, setFullNotes] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isRTL = i18n.language === 'he';

  // Sync with parent-controlled editing state - close if parent says we're not editing
  useEffect(() => {
    if (isEditingProp === false) {
      // Parent wants to close this editor (another one was opened)
      if (isEditing) {
        setIsEditing(false);
        setEditValue(fullNotes);
      }
    }
  }, [isEditingProp, isEditing, fullNotes]);

  useEffect(() => {
    if (hasFetched) return;
    
    const fetchPatientNotes = async () => {
      try {
        setHasFetched(true);
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const patientDocRef = doc(db, 'patients', caseId);
        const patientDoc = await getDoc(patientDocRef);
        
        if (patientDoc.exists()) {
          const firebaseData = patientDoc.data();
          const patientNotes = firebaseData.notes || '';
          setFullNotes(patientNotes);
          // Truncate notes if too long for table display
          setNotes(patientNotes.length > 50 ? patientNotes.substring(0, 50) + '...' : patientNotes);
        } else {
          setNotes('N/A');
          setFullNotes('');
        }
      } catch (err) {
        setNotes('Error Loading');
        setFullNotes('');
      }
    };

    fetchPatientNotes();
  }, [caseId, hasFetched]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const handleEdit = () => {
    if (onEditStart) {
      onEditStart(); // Notify parent to close other editors
    }
    setEditValue(fullNotes);
    setIsEditing(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const patientDocRef = doc(db, 'patients', caseId);
      await updateDoc(patientDocRef, {
        notes: editValue,
        updatedAt: new Date()
      });
      
      setFullNotes(editValue);
      setNotes(editValue.length > 50 ? editValue.substring(0, 50) + '...' : editValue);
      setIsEditing(false);
      
      if (onEditEnd) {
        onEditEnd(); // Notify parent that editing ended
      }
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error saving notes:', err);
      alert(t('patientDetail.updateError') || 'Error saving notes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(fullNotes);
    setIsEditing(false);
    
    if (onEditEnd) {
      onEditEnd(); // Notify parent that editing ended
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSave();
    }
  };

  if (isEditing) {
    return (
      <div 
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '4px',
          minWidth: '200px',
          maxWidth: '400px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <textarea
          ref={textareaRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            minHeight: '60px',
            padding: '6px 8px',
            border: '2px solid #007acc',
            borderRadius: '4px',
            fontSize: '13px',
            fontFamily: 'inherit',
            resize: 'vertical',
            direction: isRTL ? 'rtl' : 'ltr'
          }}
          placeholder={t('patients.noNotes')}
        />
        <div style={{ 
          display: 'flex', 
          gap: '4px',
          justifyContent: isRTL ? 'flex-start' : 'flex-end'
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
            disabled={isSaving}
            style={{
              padding: '4px 12px',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.6 : 1
            }}
          >
            {isSaving ? t('patientDetail.saving') || 'Saving...' : t('patientDetail.saveChanges') || 'Save'}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCancel();
            }}
            disabled={isSaving}
            style={{
              padding: '4px 12px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.6 : 1
            }}
          >
            {t('patientDetail.cancel') || 'Cancel'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '6px',
      cursor: 'pointer',
      padding: '2px 4px',
      borderRadius: '4px',
      transition: 'background-color 0.2s'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = '#f0f0f0';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = 'transparent';
    }}
    onClick={(e) => {
      e.stopPropagation();
      handleEdit();
    }}
    title={fullNotes || t('patients.noNotes')}
    >
      <span style={{ 
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
        {notes === 'N/A' ? t('patients.noNotes') : (notes || t('patients.noNotes'))}
      </span>
    </div>
  );
}

// Google Places Search Component using working Autocomplete API
export function GooglePlacesSearch({ value, onChange, placeholder }: { value: string; onChange: (value: string) => void; placeholder: string }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);

  useEffect(() => {
    // Wait for Google Maps API to be fully loaded
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        console.log('Google Maps API loaded successfully');
        setIsGoogleLoaded(true);
      } else {
        console.log('Waiting for Google Maps API...');
        setTimeout(checkGoogleMaps, 100);
      }
    };
    
    checkGoogleMaps();
  }, []);

  useEffect(() => {
    if (isGoogleLoaded && inputRef.current && !autocompleteRef.current) {
      console.log('Initializing Google Places Autocomplete');
      
      // Suppress the deprecation warning by catching it
      const originalWarn = console.warn;
      console.warn = (...args) => {
        if (args[0] && args[0].includes && args[0].includes('google.maps.places.Autocomplete')) {
          // Skip the deprecation warning
          return;
        }
        originalWarn.apply(console, args);
      };

      try {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ['address'],
          fields: ['formatted_address', 'geometry', 'name']
        });

        console.log('Autocomplete initialized successfully');

        // Add place selection listener
        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current?.getPlace();
          console.log('Place selected:', place);
          if (place?.formatted_address) {
            onChange(place.formatted_address);
          }
        });
      } catch (error) {
        console.error('Error initializing Google Places Autocomplete:', error);
      } finally {
        // Restore original console.warn
        console.warn = originalWarn;
      }
    }

    return () => {
      if (autocompleteRef.current && window.google && window.google.maps && window.google.maps.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isGoogleLoaded, onChange]);

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="form-input"
      style={{ paddingLeft: '40px' }}
    />
  );
}

// Component to display user email from Firebase using user ID
export function PatientCreatedByDisplay({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>(t('common.loading'));
  const [name, setName] = useState<string>('');
  const [picture, setPicture] = useState<string>('');
  const [error] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  useEffect(() => {
    // Prevent multiple calls for the same userId
    if (hasFetched) return;
    
    const fetchUserData = async () => {
      try {
        setHasFetched(true);
        
        // If userId is already an email, use it directly
        if (userId.includes('@')) {
          setEmail(userId);
          setName(userId.split('@')[0]); // Use part before @ as name
          setPicture(''); // No picture for email-only users
          return;
        }
        
        // Fetch user data from Firebase
        const userDocRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data() as any;
          if (userData.email) {
            setEmail(userData.email);
            setName(userData.name || userData.email.split('@')[0]);
            setPicture(userData.picture || '');
          } else {
            setEmail('Unknown User');
            setName('?');
            setPicture('');
          }
        } else {
          setEmail('User Not Found');
          setName('?');
          setPicture('');
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setEmail('Error Loading');
        setName('?');
        setPicture('');
      }
    };

    if (userId) {
      fetchUserData();
    } else {
      setEmail('Unknown');
      setName('?');
      setPicture('');
    }
  }, [userId, hasFetched]);

  if (error) {
    return <span style={{ color: '#ff6b6b' }}>Error</span>;
  }

  // Show profile picture first, fallback to person icon
  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block'
      }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          backgroundColor: picture ? 'transparent' : '#6c757d',
          color: 'white',
          fontSize: '12px',
          cursor: 'pointer',
          overflow: 'hidden',
          border: picture ? '1px solid #ddd' : 'none'
        }}
      >
        {picture ? (
          <img
            src={picture}
            alt="Profile"
            onError={(e) => {
              // Hide the image and show icon instead when image fails to load
              e.currentTarget.style.display = 'none';
              setPicture('');
            }}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <span style={{ fontSize: '12px' }}>👤</span>
        )}
      </div>
      
      {showTooltip && (
        <div
          style={{
            position: 'absolute',
            bottom: '30px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#333',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            whiteSpace: 'nowrap',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}
        >
          {email}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '4px solid #333'
            }}
          />
        </div>
      )}
    </div>
  );
}

// Component to display assigned user avatar with email on hover
export function AssignedUserAvatar({ 
  userId, 
  onClick, 
  canRemove = false 
}: { 
  userId: string;
  onClick?: (e: React.MouseEvent) => void;
  canRemove?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [email, setEmail] = useState<string>(t('common.loading'));
  const [name, setName] = useState<string>('');
  const [picture, setPicture] = useState<string>('');
  const [error] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  useEffect(() => {
    // Prevent multiple calls for the same userId
    if (hasFetched) return;
    
    const fetchUserData = async () => {
      try {
        setHasFetched(true);
        
        // If userId is already an email, use it directly
        if (userId.includes('@')) {
          setEmail(userId);
          setName(userId.split('@')[0]); // Use part before @ as name
          setPicture(''); // No picture for email-only users
          return;
        }
        
        // Fetch user data from Firebase
        const userDocRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data() as any;
          if (userData.email) {
            setEmail(userData.email);
            setName(userData.name || userData.email.split('@')[0]);
            setPicture(userData.picture || '');
          } else {
            setEmail('Unknown User');
            setName('?');
            setPicture('');
          }
        } else {
          setEmail('User Not Found');
          setName('?');
          setPicture('');
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setEmail('Error Loading');
        setName('?');
        setPicture('');
      }
    };

    if (userId) {
      fetchUserData();
    } else {
      setEmail('Unknown');
      setName('?');
      setPicture('');
    }
  }, [userId, hasFetched]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showPopup) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showPopup]);

  if (error) {
    return <span style={{ color: '#ff6b6b' }}>Error</span>;
  }

  // Show profile picture first, fallback to person icon
  const handleAvatarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canRemove && onClick) {
      setShowPopup(!showPopup);
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block'
      }}
      onMouseEnter={() => {
        if (!canRemove) {
          setShowTooltip(true);
        }
      }}
      onMouseLeave={() => {
        if (!canRemove) {
          setShowTooltip(false);
        }
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: picture ? 'transparent' : '#6c757d',
          color: 'white',
          fontSize: '10px',
          cursor: canRemove && onClick ? 'pointer' : 'default',
          margin: '1px',
          overflow: 'hidden',
          border: picture ? '1px solid #ddd' : 'none',
          transition: 'all 0.2s ease'
        }}
        onClick={handleAvatarClick}
      >
        {picture ? (
          <img
            src={picture}
            alt="Profile"
            onError={(e) => {
              // Hide the image and show icon instead when image fails to load
              e.currentTarget.style.display = 'none';
              setPicture('');
            }}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <span style={{ fontSize: '10px' }}>👤</span>
        )}
      </div>
      
      {/* Simple tooltip for non-removable users */}
      {showTooltip && !canRemove && (
        <div
          style={{
            position: 'absolute',
            bottom: '25px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#333',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            whiteSpace: 'nowrap',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}
        >
          {email}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '4px solid #333'
            }}
          />
        </div>
      )}
      
      {/* Popup window for removable users */}
      {showPopup && canRemove && onClick && (
        <div
          style={{
            position: 'absolute',
            top: '30px',
            ...(isRTL ? { right: '0' } : { left: '50%', transform: 'translateX(-50%)' }),
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '16px',
            minWidth: '200px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            zIndex: 1001,
            border: '1px solid #e0e0e0'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            {picture ? (
              <img
                src={picture}
                alt="Profile"
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #e0e0e0'
                }}
              />
            ) : (
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}
              >
                {name ? name.charAt(0).toUpperCase() : '👤'}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                {name || email.split('@')[0]}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {email}
              </div>
            </div>
          </div>
          
          {canRemove && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick(e);
                setShowPopup(false);
              }}
              style={{
                width: '100%',
                padding: '8px 16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#c82333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#dc3545';
              }}
            >
              {t('patientDetail.unassign') || 'Unassign'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Component to display patient details in a hover tooltip
export function PatientHoverTooltip({ caseId, children }: { caseId: string; children: React.ReactNode }) {
  const { t, i18n } = useTranslation();
  const [patientData, setPatientData] = useState<any>(null);
  const [nextTask, setNextTask] = useState<any>(null);
  const [treatmentPlan, setTreatmentPlan] = useState<any>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const hoverElementRef = useRef<HTMLElement | null>(null);

  const fetchPatientData = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      // Fetch patient info
      const patientResponse = await fetch(getApiUrl(`/api/patients/${caseId}`));
      if (patientResponse.ok) {
        const data = await patientResponse.json();
        setPatientData(data.patient);
      }

      // Fetch next task
      const { collection, query, where, orderBy, limit, getDocs } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      const tasksRef = collection(db, 'tasks');
      const tasksQuery = query(
        tasksRef,
        where('caseId', '==', caseId),
        where('status', '!=', 'completed'),
        orderBy('dueDate', 'asc'),
        limit(1)
      );
      const tasksSnapshot = await getDocs(tasksQuery);
      if (!tasksSnapshot.empty) {
        setNextTask({ id: tasksSnapshot.docs[0].id, ...tasksSnapshot.docs[0].data() });
      } else {
        setNextTask(null);
      }

      // Fetch treatment plan (milestones)
      const milestonesRef = collection(db, 'milestones');
      const milestonesQuery = query(
        milestonesRef, 
        where('caseId', '==', caseId),
        orderBy('createdAt', 'desc')
      );
      const milestonesSnapshot = await getDocs(milestonesQuery);
      if (!milestonesSnapshot.empty) {
        const milestones = milestonesSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        setTreatmentPlan({ milestones });
      } else {
        setTreatmentPlan(null);
      }
    } catch (error) {
      console.error('Error fetching patient data for tooltip:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateTooltipPosition = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const tooltipWidth = 400; // maxWidth from tooltip styles
    const tooltipHeight = 500; // estimated height
    const padding = 10; // distance from element
    const edgePadding = 20; // distance from screen edges
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate initial position (below element)
    let top = rect.bottom + padding;
    let left = i18n.language === 'he' ? rect.right - tooltipWidth : rect.left;
    
    // Check if tooltip goes off right edge
    if (left + tooltipWidth + edgePadding > viewportWidth) {
      left = viewportWidth - tooltipWidth - edgePadding;
    }
    
    // Check if tooltip goes off left edge
    if (left < edgePadding) {
      left = edgePadding;
    }
    
    // Check if tooltip goes off bottom edge
    if (top + tooltipHeight + edgePadding > viewportHeight) {
      // Try to position above element instead
      const topAbove = rect.top - tooltipHeight - padding;
      if (topAbove > edgePadding) {
        top = topAbove;
      } else {
        // If it doesn't fit above either, center vertically
        top = Math.max(edgePadding, (viewportHeight - tooltipHeight) / 2);
      }
    }
    
    // Check if tooltip goes off top edge
    if (top < edgePadding) {
      top = edgePadding;
    }
    
    setTooltipPosition({ top, left });
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    hoverElementRef.current = e.currentTarget as HTMLElement;
    updateTooltipPosition(e.currentTarget as HTMLElement);
    
    timeoutRef.current = setTimeout(() => {
      fetchPatientData();
      setShowTooltip(true);
    }, 300); // Small delay before showing tooltip
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowTooltip(false);
    hoverElementRef.current = null;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (showTooltip && hoverElementRef.current) {
      updateTooltipPosition(e.currentTarget as HTMLElement);
    }
  };

  const translateValue = (field: string, value: string) => {
    if (!value || value === '') return '—';
    
    switch (field) {
      case 'gender':
        return value === 'male' ? t('intakeForm.male') : t('intakeForm.female');
      case 'marital_status':
        return value === 'single' ? t('patientDetail.single') : t('patientDetail.married');
      case 'education':
        switch (value) {
          case 'elementary': return t('patientDetail.elementarySchool');
          case 'high_school': return t('patientDetail.highSchool');
          case 'college': return t('patientDetail.college');
          case 'university': return t('patientDetail.university');
          case 'graduate': return t('patientDetail.graduateDegree');
          case 'other': return t('patientDetail.other');
          default: return value;
        }
      default:
        return value;
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Clone children and add event handlers
  const childrenWithHandlers = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<any>, {
        onMouseEnter: (e: React.MouseEvent) => {
          handleMouseEnter(e);
          if ((child as any).props?.onMouseEnter) {
            (child as any).props.onMouseEnter(e);
          }
        },
        onMouseLeave: (e: React.MouseEvent) => {
          handleMouseLeave();
          if ((child as any).props?.onMouseLeave) {
            (child as any).props.onMouseLeave(e);
          }
        },
        onMouseMove: (e: React.MouseEvent) => {
          handleMouseMove(e);
          if ((child as any).props?.onMouseMove) {
            (child as any).props.onMouseMove(e);
          }
        }
      });
    }
    return child;
  });

  return (
    <>
      {childrenWithHandlers}
      {showTooltip && patientData && tooltipPosition && (
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            zIndex: 10000,
            backgroundColor: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            padding: '20px',
            minWidth: '320px',
            maxWidth: '400px',
            pointerEvents: 'none',
            ...(i18n.language === 'he' ? { direction: 'rtl' } : { direction: 'ltr' })
          }}
        >
          {/* Profile Photo */}
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            {patientData.profile_photo_url ? (
              <img
                src={patientData.profile_photo_url}
                alt={t('patientDetail.profilePhoto')}
                style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid #e9ecef'
                }}
              />
            ) : (
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                backgroundColor: '#f8f9fa',
                border: '3px solid #e9ecef',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                fontSize: '48px',
                color: '#6c757d'
              }}>
                👤
              </div>
            )}
          </div>
          
          {/* Patient Name */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '16px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e9ecef'
          }}>
            <h4 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: 'bold', 
              color: '#000000' 
            }}>
              {patientData.first_name} {patientData.last_name}
            </h4>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {t('patients.caseId')}: {caseId}
            </div>
          </div>

          {/* Patient Details Grid */}
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            fontSize: '13px'
          }}>
            {/* Phone */}
            <div>
              <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>
                📞 {t('patientDetail.phone')}
              </div>
              <div style={{ color: '#000000', fontWeight: '500' }}>
                {patientData.phone || '—'}
              </div>
            </div>

            {/* Email */}
            <div style={{ borderTop: '1px solid #e9ecef', paddingTop: '12px' }}>
              <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>
                📧 {t('patientDetail.email') || 'Email'}
              </div>
              <div style={{ color: '#000000', fontWeight: '500' }}>
                {patientData.email || '—'}
              </div>
            </div>

            {/* Next Task */}
            <div style={{ borderTop: '1px solid #e9ecef', paddingTop: '12px' }}>
              <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>
                ✅ {t('dashboard.nextTask') || 'Next Task'}
              </div>
              {nextTask ? (
                <div>
                  <div style={{ color: '#000000', fontWeight: '500' }}>
                    {nextTask.title}
                  </div>
                  {nextTask.dueDate && (
                    <div style={{ color: '#666', fontSize: '11px', marginTop: '2px' }}>
                      {formatDate(nextTask.dueDate)}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#999', fontStyle: 'italic' }}>
                  {t('dashboard.noUpcomingTasks') || 'No upcoming tasks'}
                </div>
              )}
            </div>

            {/* Treatment Plan */}
            <div style={{ borderTop: '1px solid #e9ecef', paddingTop: '12px' }}>
              <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>
                🎯 {t('dashboard.treatmentPlan') || 'Treatment Plan'}
              </div>
              {treatmentPlan && treatmentPlan.milestones && treatmentPlan.milestones.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {treatmentPlan.milestones.slice(0, 3).map((milestone: any, index: number) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '10px' }}>
                        {milestone.status === 'achieved' ? '✓' : 
                         milestone.status === 'stuck' ? '⚠️' : 
                         milestone.status === 'frozen' ? '❄️' : 
                         milestone.status === 'maintenance' ? '🔄' : '○'}
                      </span>
                      <span style={{ 
                        color: milestone.status === 'achieved' ? '#999' : '#000000', 
                        fontSize: '12px',
                        textDecoration: milestone.status === 'achieved' ? 'line-through' : 'none'
                      }}>
                        {milestone.title}
                      </span>
                    </div>
                  ))}
                  {treatmentPlan.milestones.length > 3 && (
                    <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>
                      +{treatmentPlan.milestones.length - 3} {t('common.more') || 'more'}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#999', fontSize: '12px', fontStyle: 'italic' }}>
                  {t('dashboard.noTreatmentPlan') || 'No treatment plan'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
